import React, { Fragment, memo } from 'react'
import { SecureWrapper } from '../layout/SecureWrapper'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { InsuranceCollectDataStep } from 'src/interfaces/collectData'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { FileUploadInput } from 'src/components/shared/FileUploadInput'
import { UploadedFile } from 'src/components/shared/UploadedFile'
import { NoteMajor } from '@shopify/polaris-icons'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { CommonModal } from 'src/components/shared/CommonModal'
import { UploadInsuranceDeclaration } from 'src/constants/actionTypes'
import { Button } from 'src/components/shared/Button'
import insuranceDeclarationExample from 'src/assets/images/insurance-declaration-example.png'
import { StepLabels } from '../client/step-labels/StepLabels'
import { MAX_FILE_SIZE_BYTES } from 'src/constants/fileUpload'

const ACCESS_TYPES = ['image/jpeg', 'image/png', 'application/pdf']

const InsuranceDeclarationComponent = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as InsuranceCollectDataStep

  const [showModal, setShowModal] = useState<boolean>(false)
  const [insuranceDeclarationFiles, setInsuranceDeclarationFiles] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await createAsyncAction(
        dispatch,
        UploadInsuranceDeclaration.request({
          files: insuranceDeclarationFiles,
          stepId: currentStep?.id,
        }),
      )
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUploadInsuranceDeclaration = useCallback(async (file: File) => {
    setInsuranceDeclarationFiles((prev: File[]) => [...prev, file])
  }, [])

  const handleRemoveFile = useCallback((fileIndex: number) => {
    setInsuranceDeclarationFiles((prev: File[]) => prev.filter((_, index) => index !== fileIndex))
  }, [])

  const openModal = useCallback(() => {
    setShowModal(true)
  }, [])

  const closeModal = useCallback(() => {
    setShowModal(false)
  }, [])

  return (
    <SecureWrapper
      footer={
        <Fragment>
          <FormErrorMessage css={tw`mb-4`} error={error} />
          <Button
            className={tw`w-full`}
            loading={isLoading}
            disabled={!insuranceDeclarationFiles.length}
            onClick={handleSubmit}
            data-testid="contractors-onboarding-insurance-declaration-submit-btn"
          >
            Submit and continue
          </Button>
        </Fragment>
      }
    >
      <div
        css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
      >
        <NoteMajor width={20} />
      </div>
      <StepLabels />
      <Typography css={tw`my-2`} variant="h2">
        Auto Policy Declaration Packet
      </Typography>
      <Typography css={tw`mb-4 text-shades-400`}>
        Submit your detailed Auto Policy Declarations packet. Be sure to include ALL pages, and
        ensure the coverage limits, your name, and any expiration dates are clear.
      </Typography>
      <div css={tw`mb-4`}>
        <InfoContainer
          onClick={openModal}
          dataTestId="contractors-onboarding-insurance-declaration-info-btn"
        >
          Tap to get more information about Auto Policy Declarations packet
        </InfoContainer>
      </div>
      <div css={tw`mt-6`}>
        <Typography css={tw`uppercase mb-2 text-shades-400`} variant="overline">
          Upload Auto Policy Declaration Packet
        </Typography>
        <div css={tw`bg-shades-0 p-4 rounded-xl mt-2`}>
          {insuranceDeclarationFiles.map((file, index) => (
            <UploadedFile
              key={index}
              file={file}
              onRemove={() => {
                handleRemoveFile(index)
              }}
              removeButtonTestId={`contractors-onboarding-insurance-declaration-uploaded-${index}-remove-btn`}
            />
          ))}
          {insuranceDeclarationFiles.length < 5 && (
            <Fragment>
              <FileUploadInput
                fileSizeLimit={MAX_FILE_SIZE_BYTES}
                acceptTypes={ACCESS_TYPES}
                onSelect={handleUploadInsuranceDeclaration}
                inputDataTestId="contractors-onboarding-insurance-declaration-file-input"
              />
              <Typography css={tw`mt-4 text-shades-400 text-center`} variant="body1">
                After you upload a file, you'll be able to upload another one
              </Typography>
            </Fragment>
          )}
        </div>
      </div>
      <CommonModal
        isOpen={showModal}
        title="About insurance amount declaration"
        text="Follow the provided examples."
        image={insuranceDeclarationExample}
        onClose={closeModal}
        closeButtonTestId="contractors-onboarding-insurance-declaration-modal-close-btn"
      />
    </SecureWrapper>
  )
}

export const InsuranceDeclaration = memo(InsuranceDeclarationComponent)
